import { hasYesOrNo, checkValue } from 'models/validate';
import address from 'forms/shared/validators/models/location/models/address';
import phoneModelValidator from 'forms/shared/validators/models/phone/phone';
import emailModelValidator from 'forms/shared/validators/models/pvqEmail';
import name from 'forms/shared/validators/models/name/name';
import { DEFAULT_LATEST, THIS_MONTH } from 'constants/dateLimits';
import { ValidateJSModel } from 'forms/shared/types/validatejs';
import { substanceDrugTypeValidator } from 'forms/sections/substance/shared/drugTypeValidator';
import { getSafeDateTime, isSafeDateLater, setToStartOfMonth } from 'helpers/date';
import { formConfig } from 'config/forms';
import { SafeDateTime } from 'helpers/types';
import { counselingRecommendedByOptions, drugTypes } from 'constants/enums/substanceOptions';
import { REQUIRED } from 'constants/errors';
import { isPVQForm } from 'helpers/form';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { isSafeUnitedStates } from 'helpers/locations';
import { SubstanceDrugsVoluntaryTreatmentItemModel } from '../types';

export const substanceDrugsVoluntaryTreatmentItemValidator: ValidateJSModel<SubstanceDrugsVoluntaryTreatmentItemModel> = {
  DrugType: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { };
    }
    return substanceDrugTypeValidator;
  },
  // TODO - add this back after fixing DrugType structure
  /*
  DrugType: { presence: true, hasValue: { validator: { inclusion: drugTypes } } },
  DrugTypeExplanation: (value, attributes) => {
    if (attributes?.DrugType && attributes?.DrugType.value === 'Other') {
      return { presence: true, hasValue: true }
    }
    return {}
  },
  */
  RecommendedBy: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return {
        presence: true,
        array: {
          validator: {
            presence: true,
            inclusion: counselingRecommendedByOptions,
          },
          length: { minimum: 1, message: REQUIRED },
        },
      };
    }
    return {};
  },

  RecommendedOther: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.RecommendedBy?.values?.includes('Other')) {
        return { presence: true, hasValue: true };
      }
    }

    return {};
  },

  DueToPrescription: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    return {};
  },

  PrescriptionDrug: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.DueToPrescription?.value === 'Yes') {
        return { presence: true, hasValue: true };
      }
    }
    return {};
  },

  DueToDrugs: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    return {};
  },

  DrugTypes: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.DueToDrugs?.value === 'Yes') {
        return {
          presence: true,
          array: {
            validator: {
              presence: true,
              inclusion: drugTypes,
            },
            length: { minimum: 1, message: REQUIRED },
          },
        };
      }
    }
    return {};
  },

  DrugTypeOther: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.DrugTypes?.values?.includes('Other')) {
        return { presence: true, hasValue: true };
      }
    }
    return {};
  },

  TreatmentProviderInPractice: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    return { };
  },
  TreatmentProviderTelephones: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        branchCollection: {
          validator: {
            Telephone: {
              model: {
                validator: phoneModelValidator,
                requireNumberType: true,
              },
            },
          },
        },
      };
    }
    return {};
  },
  TreatmentProviderEmails: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (value?.items?.[0]?.Item?.Has?.value === 'IDontKnow') {
        return {};
      }

      return {
        presence: true,
        branchCollection: {
          validator: {
            Email: {
              model: { validator: emailModelValidator },
            },
          },
        },
      };
    }
    return {};
  },

  TreatmentStatus: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.TreatmentCompleted?.value === 'No') {
        return { presence: true, hasValue: true };
      }
    }
    return {};
  },

  TreatmentProviderNotApplicable: {},

  TreatmentProvider: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options.formType);
    if (isPVQ) {
      if (attributes?.TreatmentProviderNotApplicable?.applicable !== false) {
        return {
          presence: true,
          model: {
            validator: pvqNameModelValidator,
            requireMiddleName: false
          }
        };
      }
      return {};
    }

    return { presence: true, model: { validator: name, hideMiddleName: true } };
  },
  TreatmentProviderPracticeNameNotApplicable: {},
  TreatmentProviderPracticeName: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.TreatmentProviderPracticeNameNotApplicable?.applicable !== false) {
        return { presence: true, hasValue: true };
      }
    }
    return {};
  },

  TreatmentProviderAddress: (value, attributes, attributeName, options = {}) => {
    if (isPVQForm(options?.formType)) {
      return {
        location: {
          validator: pvqLocationModelValidator,
          requireStreet: isSafeUnitedStates(value),
          requireCity: true,
          requireMilitaryDiplomaticInstallationName: true,
          requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
        },
        presence: true,
      };
    }
    return { presence: true, location: { validator: address } };
  },

  TreatmentProviderTelephone: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return {};
    }
    return {
      presence: true,
      model: { validator: phoneModelValidator, requireNumber: true }
    };
  },
  TreatmentDates: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (!options?.applicantBirthdate) { return {}; }
      if (!value?.to) { return { presence: true }; }
      const applicantBirthdate: SafeDateTime = getSafeDateTime(options?.applicantBirthdate);
      const endDate: SafeDateTime = getSafeDateTime(value.to);
      const sixteenthBirthdate: SafeDateTime = getSafeDateTime(applicantBirthdate?.datetime?.plus({ years: 16 }));
      const twentyFirstBirthday: SafeDateTime = getSafeDateTime(applicantBirthdate?.datetime?.plus({ years: 21 }));
      const fiveYearsAgo: SafeDateTime = getSafeDateTime(DEFAULT_LATEST.minus({ years: 5 }));

      // if 21 bday is before (or on) today applicant is over 21
      const isOverTwentyOne: boolean = isSafeDateLater(getSafeDateTime(DEFAULT_LATEST), twentyFirstBirthday);
      // five years for 21 yoe, any time after 16 for under age.
      const stauteOfLimitation: SafeDateTime = isOverTwentyOne ? fiveYearsAgo : sixteenthBirthdate;

      const isEndDateBeforeStatue: boolean = isSafeDateLater(stauteOfLimitation, endDate);

      return {
        presence: true,
        daterange: {
          requireYear: false,
          earliest: isEndDateBeforeStatue ? setToStartOfMonth(stauteOfLimitation).datetime : applicantBirthdate?.datetime,
          tooEarly: isEndDateBeforeStatue ? 'error.beforeDuration' : 'error.applicantMin',
          latest: DEFAULT_LATEST
        },
      };
    }

    const formTypeConfig = options?.formType && formConfig[options?.formType as keyof typeof formConfig];
    const years: any = formTypeConfig && formTypeConfig.SUBSTANCE_DRUG_TREATMENT_YEARS;

    const beyondRequired: SafeDateTime = getSafeDateTime(THIS_MONTH);
    let useDuration = false;

    if (years && beyondRequired?.valid && typeof years === 'number') {
      beyondRequired.datetime = beyondRequired.datetime.minus({ years });
      useDuration = !value?.present && isSafeDateLater(beyondRequired, setToStartOfMonth(getSafeDateTime(value?.to)));
    }

    return {
      presence: true,
      daterange: {
        requireYear: false,
        earliest: useDuration ? setToStartOfMonth(beyondRequired).datetime : setToStartOfMonth(options?.applicantBirthdate),
        tooEarly: useDuration ? 'error.beforeDuration' : 'error.applicantMin',
        latest: DEFAULT_LATEST
      },
    };
  },
  TreatmentCompleted: { presence: true, hasValue: { validator: hasYesOrNo } },
  NoTreatmentExplanation: (value, attributes) => {
    if (checkValue(attributes?.TreatmentCompleted, 'No')) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  Comments: {},
  NoTreatmentComments: {},
};
